/**
 * Analyze / reanalyze one video with tier and frame-rate options. Shared by the Videos list and the detail page.
 * `AnalyzeOptions` is reused by the bulk dialog on the Videos page.
 */
import { useState, type FormEvent } from 'react';
import { Alert } from '@/components/arc/alert/alert';
import { Button } from '@/components/arc/button/button';
import { Dialog, DialogClose, DialogContent } from '@/components/arc/dialog/dialog';
import { RadioGroup } from '@/components/arc/radio-group/radio-group';
import { Select } from '@/components/arc/select/select';
import { useToastStack } from '@/components/arc/toast-stack/toast-stack';
import { useAnalyzeVideo, useReanalyzeVideo } from '@/api/hooks';
import { errorMessage } from '@/api/client';
import type { AnalysisTier, VideoOut } from '@/api/types';
import { truncate } from '@/lib/format';
import styles from './AnalyzeDialog.module.css';

export const TIER_OPTIONS: { value: AnalysisTier; label: string; description: string }[] = [
  { value: 'standard', label: 'Standard', description: 'Default model. Lowest cost per video minute.' },
  { value: 'premium', label: 'Premium', description: 'Larger model. Higher accuracy at a higher cost.' },
];

export const FPS_OPTIONS = [
  { value: 'auto', label: 'Auto (1 fps, 2 fps for sports and gaming)' },
  { value: '1', label: '1 fps' },
  { value: '2', label: '2 fps' },
];

export function fpsValue(fps: string): number | null {
  return fps === 'auto' ? null : Number(fps);
}

export function AnalyzeOptions({
  tier,
  onTierChange,
  fps,
  onFpsChange,
}: {
  tier: AnalysisTier;
  onTierChange: (tier: AnalysisTier) => void;
  fps: string;
  onFpsChange: (fps: string) => void;
}) {
  return (
    <>
      <RadioGroup
        label="Tier"
        options={TIER_OPTIONS}
        value={tier}
        onValueChange={(v) => onTierChange(v as AnalysisTier)}
      />
      <Select
        label="Frame rate"
        options={FPS_OPTIONS}
        value={fps}
        onValueChange={onFpsChange}
        description="A higher frame rate catches faster scene changes and costs more."
      />
    </>
  );
}

export interface AnalyzeDialogProps {
  /** The video to analyze. `null` closes the dialog. */
  video: VideoOut | null;
  onClose: () => void;
}

export function AnalyzeDialog({ video, onClose }: AnalyzeDialogProps) {
  // Keep the last video while the dialog animates out, so the title does not blank.
  const [shown, setShown] = useState<VideoOut | null>(video);
  if (video && video !== shown) setShown(video);
  const reanalyze = shown?.analysis_status === 'done';
  return (
    <Dialog open={!!video} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={reanalyze ? 'Reanalyze video' : 'Analyze video'}
        description={shown ? truncate(shown.title || shown.external_id, 80) : undefined}
      >
        {shown && <AnalyzeForm key={shown.id} video={shown} reanalyze={!!reanalyze} onDone={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function AnalyzeForm({
  video,
  reanalyze,
  onDone,
}: {
  video: VideoOut;
  reanalyze: boolean;
  onDone: () => void;
}) {
  const { toast } = useToastStack();
  const analyze = useAnalyzeVideo();
  const reanalyzeMut = useReanalyzeVideo();
  const mutation = reanalyze ? reanalyzeMut : analyze;
  const [tier, setTier] = useState<AnalysisTier>(video.analysis_tier ?? 'standard');
  const [fps, setFps] = useState('auto');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await mutation.mutateAsync({ id: video.id, body: { tier, fps: fpsValue(fps) } });
      toast({
        type: 'success',
        title: reanalyze ? 'Reanalysis queued' : 'Analysis queued',
        description: truncate(video.title || video.external_id, 60),
      });
      onDone();
    } catch {
      /* shown inline below */
    }
  };

  return (
    <form onSubmit={submit} className={styles.form}>
      <AnalyzeOptions tier={tier} onTierChange={setTier} fps={fps} onFpsChange={setFps} />
      {reanalyze && (
        <p className={styles.note}>The previous analysis stays in storage. Moments are replaced.</p>
      )}
      {mutation.isError && (
        <Alert tone="danger" title="Could not queue the analysis">
          {errorMessage(mutation.error)}
        </Alert>
      )}
      <div className={styles.actions}>
        <DialogClose asChild>
          <Button type="button" variant="secondary">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit" loading={mutation.isPending}>
          {reanalyze ? 'Reanalyze video' : 'Analyze video'}
        </Button>
      </div>
    </form>
  );
}
